import ChartBox from '@mdi/svg/svg/chart-box-outline.svg?react';
import AIIcon from '@mdi/svg/svg/creation.svg?react';
import Equalizer from '@mdi/svg/svg/equalizer.svg?react';
import FolderSearchIcon from '@mdi/svg/svg/folder-search.svg?react';
import ShieldSearch from '@mdi/svg/svg/shield-search.svg?react';
import Shield from '@mdi/svg/svg/shield.svg?react';
import Video from '@mdi/svg/svg/video.svg?react';
import ViewModule from '@mdi/svg/svg/view-module.svg?react';

import {
  canAccessAlarmStatusOnly,
  canViewAlarmsPage,
  canViewDataExplorerVerifEye,
  canViewVerifeyePage,
  userHasFeature,
  type UserSession,
} from '@solinkcorp/authentication';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { DATA_EXPLORER_FLAGS } from '@solinkcorp/native-data-explorer/featureFlags';
import { ComponentLoadingSpinner, MDIIcon, Speedometer } from '@solinkcorp/solink-components';
import Clip from '@solinkcorp/solink-components/svg/clip.svg?react';
import EventActions from 'actions/EventActions';
import { resetCameraGroup } from 'actions/filterActions';
import Loadable from 'components/Loadable';
import { type NavTabDescriptor } from 'components/NavigationFrame/DesktopNav/NavTab';
import { Database } from 'lucide-react';
import React from 'react';
import { type DispatchType } from 'stores/store';
import FeaturesUtil from 'utils/FeaturesUtil';
import { VERIFEYE4_URL } from 'utils/urls';
import {
  AGENTS_PAGE,
  AGENTS_URL,
  ALARMS_PAGE,
  CLIPS_PAGE,
  DASHBOARD_PAGE,
  DATA_EXPLORER_PAGE,
  DATA_EXPLORER_URL,
  DATA_EXPLORER_VERIFEYE_PAGE,
  DATA_EXPLORER_VERIFEYE_URL,
  DATASETS_PAGE,
  DATASETS_URL,
  DISCOVER_PAGE,
  EVENTS_PAGE,
  INVESTIGATIONS_PAGE,
  INVESTIGATIONS_URL,
  VERIFEYE4_PAGE,
  VIDEO_PAGE,
} from 'vd-constants';
import { type PlayingCamerasController } from '../VideoPlayer/controller/PlayingCamerasController';

const EventsPage = Loadable({
  loader: () => import('../EventsPage/EventsPage'),
  loading: ComponentLoadingSpinner,
});

const HomePage = Loadable({
  loader: () => import('components/HomePage'),
  loading: ComponentLoadingSpinner,
});

const VideoPage = Loadable({
  loader: () => import('../VideoPage/VideoPage'),
  loading: ComponentLoadingSpinner,
});

const DiscoverPage = Loadable({
  loader: () => import('../DiscoverPage/Discover'),
  loading: ComponentLoadingSpinner,
});

const ClipsPage = Loadable({
  loader: () => import('../ClipsPage/ClipsPage'),
  loading: ComponentLoadingSpinner,
});

const AlarmsPage = Loadable({
  loader: () => import('../AlarmsPage/AlarmsPage'),
  loading: ComponentLoadingSpinner,
});

const AgentsPage = Loadable({
  loader: () => import('@solinkcorp/agent/AgentsPage/AgentsPage'),
  loading: ComponentLoadingSpinner,
});

const DatasetsPage = Loadable({
  loader: () => import('@solinkcorp/agent/DatasetsPage/DatasetsPage'),
  loading: ComponentLoadingSpinner,
});

const InvestigationsPage = Loadable({
  loader: () =>
    import('components/InvestigationsPage/InvestigationsPage').then((m) => ({
      default: m.InvestigationsPage,
    })),
  loading: ComponentLoadingSpinner,
});

const DataExplorerPage = Loadable({
  loader: () => import('@solinkcorp/native-data-explorer/DataExplorerPage'),
  loading: ComponentLoadingSpinner,
});

const VerifEye4Routes = Loadable({
  loader: () => import('../VerifEye4Page/VerifEye4Routes'),
  loading: ComponentLoadingSpinner,
});

const DataExplorerVerifEyeRoutes = Loadable({
  loader: () => import('@solinkcorp/native-data-explorer/verifEye/DataExplorerVerifEyeRoutes'),
  loading: ComponentLoadingSpinner,
});

export interface NavTabWithComponent extends NavTabDescriptor {
  component: React.ComponentType;
}

/**
 * Generate the menu for the main navigation on the left side of the screen.
 *
 * Since the user and locale change infrequently,
 */
export const generateMenu = (
  dispatch: DispatchType,
  locale: LocaleDefinition,
  noVideoAccess: boolean,
  playingCamerasController: PlayingCamerasController,
  user: UserSession,
): NavTabWithComponent[] => {
  const resetPlayback = () => {
    playingCamerasController.clearPlayingCameras();
    EventActions.closeEventPlayer();
    dispatch(resetCameraGroup());
  };

  return [
    {
      show: FeaturesUtil.canViewDashboardPage(),
      label: locale.navigation.dashboard,
      icon: { node: <Speedometer /> },
      route: '/dashboard',
      page: DASHBOARD_PAGE,
      group: 'main',
      onClick: resetPlayback,
      component: HomePage,
    },
    {
      show: FeaturesUtil.canViewEventsPage(),
      label: locale.navigation.events,
      icon: { node: <MDIIcon icon={ViewModule} /> },
      route: '/events',
      page: EVENTS_PAGE,
      group: 'monitor',
      onClick: resetPlayback,
      component: EventsPage,
    },
    {
      show: !noVideoAccess && FeaturesUtil.canViewVideoPage(),
      label: locale.navigation.videos,
      icon: { node: <MDIIcon icon={Video} /> },
      route: '/video',
      href: '/video/list',
      page: VIDEO_PAGE,
      group: 'monitor',
      onClick: () => {
        playingCamerasController.clearPlayingCameras();
        EventActions.clearActiveEvent();
      },
      component: VideoPage,
    },
    {
      show: FeaturesUtil.canViewClipsPage() && !noVideoAccess,
      label: locale.navigation.clips,
      icon: { node: <MDIIcon icon={Clip} width={20} /> },
      route: '/clips',
      page: CLIPS_PAGE,
      group: 'investigate',
      onClick: resetPlayback,
      component: ClipsPage,
    },
    {
      show: canViewAlarmsPage(user),
      label: locale.alarms.alarms,
      icon: { node: <MDIIcon icon={Shield} /> },
      route: '/alarms',
      href:
        canAccessAlarmStatusOnly(user) || !userHasFeature(user, 'canViewAlarmSummary')
          ? '/alarms/configurations'
          : '/alarms?view=summary',
      page: ALARMS_PAGE,
      group: 'monitor',
      onClick: resetPlayback,
      component: AlarmsPage,
    },
    {
      show: canViewVerifeyePage(user),
      label: locale.navigation.verifeye,
      icon: { node: <MDIIcon icon={ShieldSearch} /> },
      route: VERIFEYE4_URL,
      href: `${VERIFEYE4_URL}/open`,
      page: VERIFEYE4_PAGE,
      group: 'main',
      onClick: resetPlayback,
      component: VerifEye4Routes,
    },
    {
      show: FeaturesUtil.canViewDiscoverPage(),
      label: locale.navigation.discover,
      icon: { node: <MDIIcon icon={Equalizer} /> },
      route: '/discover',
      page: DISCOVER_PAGE,
      group: 'main',
      onClick: resetPlayback,
      component: DiscoverPage,
    },
    {
      show:
        !FeaturesUtil.isLiveViewOnly() &&
        (userHasFeature(user, 'aiAgentEnabled') ||
          userHasFeature(user, 'aiAgentRead') ||
          userHasFeature(user, 'aiAgentManage')),
      label: 'Agents',
      icon: { node: <MDIIcon icon={AIIcon} /> },
      route: AGENTS_URL,
      page: AGENTS_PAGE,
      group: 'workspace',
      onClick: resetPlayback,
      component: AgentsPage,
    },
    {
      show: !FeaturesUtil.isLiveViewOnly() && userHasFeature(user, 'aiDatasetsEnabled'),
      label: 'Datasets',
      icon: { node: <MDIIcon icon={Database} /> },
      route: DATASETS_URL,
      page: DATASETS_PAGE,
      group: 'workspace',
      onClick: resetPlayback,
      component: DatasetsPage,
    },
    {
      // Standalone Data Explorer VerifEye reviewer tab (SL-15900). Gated solely on
      // the access flag — deliberately independent of Data Explorer base access and
      // Events-page access, so a pure reviewer sees it with no other DE entitlement.
      show: canViewDataExplorerVerifEye(user),
      // Shares the VerifEye4 tab's `verifeye` label deliberately: the two gates are
      // mutually exclusive in practice, so only one tab ever shows to a real user.
      // Both appearing at once (which would make the shared label — and the testid
      // NavTab derives from it — ambiguous) only happens on dev accounts.
      label: locale.navigation.verifeye,
      icon: { node: <MDIIcon icon={Shield} /> },
      route: DATA_EXPLORER_VERIFEYE_URL,
      href: `${DATA_EXPLORER_VERIFEYE_URL}/open`,
      page: DATA_EXPLORER_VERIFEYE_PAGE,
      group: 'main',
      onClick: resetPlayback,
      component: DataExplorerVerifEyeRoutes,
      beta: true,
    },
    {
      show: userHasFeature(user, DATA_EXPLORER_FLAGS.BASE) && !canAccessAlarmStatusOnly(user),
      label: locale.navigation.insights,
      icon: { node: <MDIIcon icon={ChartBox} /> },
      route: DATA_EXPLORER_URL,
      page: DATA_EXPLORER_PAGE,
      group: 'main',
      onClick: resetPlayback,
      component: DataExplorerPage,
      beta: true,
    },
    // Keep Investigations last in this array so legacy DesktopNav (flag-off)
    // order matches develop. Aspen reorders within the investigate group via
    // `useNavGroups` so Investigations still appears above Clips in the rail.
    {
      show: userHasFeature(user, 'investigations'),
      label: locale.navigation.investigations,
      icon: { node: <MDIIcon icon={FolderSearchIcon} /> },
      route: INVESTIGATIONS_URL,
      page: INVESTIGATIONS_PAGE,
      group: 'investigate',
      onClick: resetPlayback,
      component: InvestigationsPage,
    },
  ];
};
